/**
 * Module-level translations. Modules keep their own locale files (en.ts, th.ts) and read the
 * current language from appStore.
 *
 * Two entry points, same resolution rules:
 *   - `useTranslation(locales)` — the hook, for components. Re-renders on language change.
 *   - `createTranslator(locales)` — a plain function, for code that runs outside React
 *     (query callbacks, toast helpers). Reads the language at call time, so it always
 *     reflects the current setting, but it cannot trigger a re-render.
 *
 * Keys may be flat (t('title')) or dot-paths into nested objects
 * (t('navigation.sidebar.leads')). `{{name}}` placeholders are filled from an optional params
 * object (t('changeStatusTo', { label: 'Verified' })). An unresolved key returns the key
 * itself, which makes a missing translation visible rather than blank — unless the caller passed
 * `defaultValue`, which is then returned in its place (the i18next contract the ops module was
 * written against; keys built from API data rely on it).
 */

import { useCallback } from 'react';
import { useAppStore, getLanguageCode, type LocaleCode } from '../store/appStore';

export type TranslationParams = Record<string, string | number>;
export type Locales = { en: Record<string, unknown>; th: Record<string, unknown> };

/** The shape of `t` returned by both entry points — for helpers that take a translator. */
export type TranslateFn = (key: string, params?: TranslationParams) => string;

function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object' || !(part in (current as object))) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/** Replace `{{name}}` placeholders in a resolved string with values from params. */
function interpolate(value: string, params?: TranslationParams): string {
  if (!params) return value;
  return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

function resolve(localeMap: Record<string, unknown>, key: string, params?: TranslationParams): string {
  if (typeof key !== 'string') return '';
  const value = getNestedValue(localeMap, key);
  if (value == null) {
    // i18next honoured `defaultValue`; the ops module was ported against that contract, so a key
    // built from API data (a document type, a status) falls back to its humanised form instead of
    // rendering the key itself.
    const fallback = params?.defaultValue;
    return typeof fallback === 'string' ? interpolate(fallback, params) : key;
  }
  return interpolate(value, params);
}

export function useTranslation(locales: Locales): {
  t: (key: string, params?: TranslationParams) => string;
  locale: LocaleCode;
} {
  const language = useAppStore((state) => state.language);
  const localeMap = language === 'en' ? locales.en : locales.th;

  const t = useCallback(
    (key: string, params?: TranslationParams): string => resolve(localeMap, key, params),
    [localeMap],
  );

  return { t, locale: language };
}

/**
 * Translator for non-React call sites. The language is read on every call rather than
 * captured, so a translator created at module scope stays correct after a language switch.
 */
/**
 * Translate a key that may legitimately not exist, showing `fallback` instead of the raw key.
 *
 * For keys built from API data — a status code, a payment method, a free-text reason — where the
 * set of values is open and the locale files cannot cover it. Not a general default: a static key
 * that needs a fallback is a key missing from the locale files, and belongs there instead.
 */
export function translateOr(t: TranslateFn, key: string, fallback: string): string {
  const value = t(key);
  return value === key ? fallback : value;
}

export function createTranslator(locales: Locales): (key: string, params?: TranslationParams) => string {
  return (key, params) => resolve(getLanguageCode() === 'en' ? locales.en : locales.th, key, params);
}
